'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';
import { useInView } from '@/shared/hooks';
import { getVideoProxyUrl } from '@/shared/lib/proxy-url';
import { RevealOnScroll } from '@/shared/ui/reveal-on-scroll';
import { useLocale, useTranslations } from 'next-intl';
import styles from './SecFeatureDetailTabs.module.scss';
type FeatureTabKey = 'detail' | 'studio' | 'video' | 'banner';
type FeatureTab = {
  key: FeatureTabKey;
};
const TAB_ROTATION_MS = 15000;
const TAB_TRANSITION_MS = 1100;
// 탭 순서(FEATURE_TABS)와 매칭되는 영상 파일명.
const FEATURE_VIDEO_FILES = ['feature_1.mp4', 'feature_3.mp4', 'feature_4.mp4', 'feature_2.mp4'];

// 탭 순서는 FEATURE_VIDEO_FILES 인덱스와 매칭. 텍스트는
// intro.featureDetail.tabs.{key} 에서 해석한다(라벨/헤드라인/스텝/태그).
const FEATURE_TABS: FeatureTab[] = [{
  key: 'detail'
}, {
  key: 'banner'
}, {
  key: 'video'
}, {
  key: 'studio'
}];

// 각 탭은 3스텝 고정.
const STEP_NUMBERS = ['1', '2', '3'] as const;
function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
function easeOutCubic(progress: number) {
  const t = clamp(progress, 0, 1);
  return 1 - Math.pow(1 - t, 3);
}
function useIsMobile() {
  const $ = _c(2);
  const [isMobile, setIsMobile] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const mediaQuery = window.matchMedia("(max-width: 767px)");
      const update = () => setIsMobile(mediaQuery.matches);
      update();
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isMobile;
}
type TabButtonProps = {
  active: boolean;
  label: string;
  onClick: () => void;
  // 진행바는 부모의 rAF가 ref 로 직접 그린다(60fps 상태 재렌더 방지).
  fillRef: Ref<HTMLSpanElement>;
};
function TabButton(t0) {
  const $ = _c(11);
  const {
    active,
    label,
    onClick,
    fillRef
  } = t0;
  const labelClassName = active ? `${styles.tabLabel} ${styles.tabLabelActive}` : styles.tabLabel;
  let t1;
  if ($[0] !== label || $[1] !== labelClassName) {
    t1 = <span className={labelClassName}>{label}</span>;
    $[0] = label;
    $[1] = labelClassName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== active || $[4] !== fillRef) {
    t2 = active ? <span className={styles.tabIndicator}><span ref={fillRef} className={styles.tabIndicatorFill} style={{
        transform: "scaleX(0)"
      }} /></span> : null;
    $[3] = active;
    $[4] = fillRef;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== active || $[7] !== onClick || $[8] !== t1 || $[9] !== t2) {
    t3 = <button type="button" onClick={onClick} className={styles.tabButton} aria-pressed={active}>{t1}{t2}</button>;
    $[6] = active;
    $[7] = onClick;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function ShowcaseHero(t0) {
  const $ = _c(7);
  const {
    src
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: "400px 0px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [ref, inView] = useInView(t1);
  let t2;
  if ($[1] !== inView || $[2] !== src) {
    t2 = inView ? <video key={src} className={styles.showcaseVideo} src={src} autoPlay={true} loop={true} muted={true} playsInline={true} preload="metadata" /> : null;
    $[1] = inView;
    $[2] = src;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== ref || $[5] !== t2) {
    t3 = <div className={styles.showcase} ref={ref}>{t2}</div>;
    $[4] = ref;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function FeatureTabContent(t0) {
  const $ = _c(24);
  const {
    tab
  } = t0;
  const t = useTranslations("intro");
  const base = `featureDetail.tabs.${tab.key}` as const;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== base || $[1] !== t) {
    const tags = t.raw(`${base}.tags`) as string[];
    t3 = styles.tabContent;
    const t6 = t.rich(`${base}.headline.line1`, {
      em: _temp
    });
    let t7;
    if ($[7] !== t6) {
      t7 = <p className={styles.headlineLine}>{t6}</p>;
      $[7] = t6;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    const t8 = t.rich(`${base}.headline.line2`, {
      em: _temp2
    });
    let t9;
    if ($[9] !== t8) {
      t9 = <p className={styles.headlineLine}>{t8}</p>;
      $[9] = t8;
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    if ($[11] !== t7 || $[12] !== t9) {
      t4 = <div className={styles.headline}>{t7}{t9}</div>;
      $[11] = t7;
      $[12] = t9;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    const t10 = STEP_NUMBERS.map(number => <div key={number} className={styles.step}><div className={styles.stepNumber}>{number}</div><div className={styles.stepBody}><div className={styles.stepTitle}>{t(`${base}.steps.${number}.title`)}</div><div className={styles.stepDescription}>{t(`${base}.steps.${number}.description`)}</div></div>{number !== "3" ? <div className={styles.stepConnector} /> : null}</div>);
    if ($[14] !== t10) {
      t5 = <div className={styles.stepsList}>{t10}</div>;
      $[14] = t10;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t1 = styles.tagList;
    t2 = tags.map(_temp3);
    $[0] = base;
    $[1] = t;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[16] !== t1 || $[17] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function _temp3(tag) {
  return <div key={tag} className={styles.tag}>{tag}</div>;
}
function _temp2(chunks_0) {
  return <span className={styles.highlight}>{chunks_0}</span>;
}
function _temp(chunks) {
  return <span className={styles.highlight}>{chunks}</span>;
}
export const SecFeatureDetailTabs = () => {
  const $ = _c(39);
  const t = useTranslations("intro");
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = FEATURE_VIDEO_FILES.map(file => getVideoProxyUrl(`/commerce/static/intro/clip/${locale}/${file}`) ?? "");
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const featureVideoSrcs = t0;
  const isMobile = useIsMobile();
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const progressFillRef = useRef(null);
  const [transitionState, setTransitionState] = useState(null);
  const [transitionProgress, setTransitionProgress] = useState(1);
  const activeIndexRef = useRef(0);
  const cycleStartRef = useRef(null);
  const transitionStartRef = useRef(null);
  const transitionFrameRef = useRef(null);
  const activeTab = FEATURE_TABS[activeIndex] ?? FEATURE_TABS[0];
  let t1;
  if ($[2] !== isMobile) {
    t1 = nextIndex => {
      const normalizedIndex = (nextIndex % FEATURE_TABS.length + FEATURE_TABS.length) % FEATURE_TABS.length;
      const fromIndex = activeIndexRef.current;
      if (fromIndex === normalizedIndex) {
        cycleStartRef.current = performance.now();
        const fill = progressFillRef.current;
        if (fill) {
          fill.style.transform = "scaleX(0)";
        }
        return;
      }
      activeIndexRef.current = normalizedIndex;
      setActiveIndex(normalizedIndex);
      cycleStartRef.current = performance.now();
      if (isMobile) {
        if (transitionFrameRef.current !== null) {
          window.cancelAnimationFrame(transitionFrameRef.current);
          transitionFrameRef.current = null;
        }
        transitionStartRef.current = null;
        setTransitionState(null);
        setTransitionProgress(1);
        return;
      }
      setTransitionState({
        from: fromIndex,
        to: normalizedIndex
      });
      setTransitionProgress(0);
      if (transitionFrameRef.current !== null) {
        window.cancelAnimationFrame(transitionFrameRef.current);
      }
      transitionStartRef.current = performance.now();
    };
    $[2] = isMobile;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const switchTab = t1;
  let t2;
  let t3;
  if ($[4] !== isMobile || $[5] !== switchTab) {
    t2 = () => {
      activeIndexRef.current = 0;
      cycleStartRef.current = performance.now();
      if (isMobile) {
        setTransitionState(null);
        setTransitionProgress(1);
        return;
      }
      let rafId = null;
      let inView = false;
      const tick = now => {
        const start = cycleStartRef.current ?? now;
        const elapsed = now - start;
        const cycleProgress = clamp(elapsed / TAB_ROTATION_MS, 0, 1);
        const fill_0 = progressFillRef.current;
        if (fill_0) {
          fill_0.style.transform = `scaleX(${easeOutCubic(cycleProgress)})`;
        }
        if (elapsed >= TAB_ROTATION_MS) {
          switchTab(activeIndexRef.current + 1);
        }
        rafId = window.requestAnimationFrame(tick);
      };
      const startLoop = () => {
        if (rafId !== null) {
          return;
        }
        cycleStartRef.current = performance.now();
        const fill_1 = progressFillRef.current;
        if (fill_1) {
          fill_1.style.transform = "scaleX(0)";
        }
        rafId = window.requestAnimationFrame(tick);
      };
      const stopLoop = () => {
        if (rafId === null) {
          return;
        }
        window.cancelAnimationFrame(rafId);
        rafId = null;
      };
      const syncLoop = () => {
        if (inView && document.visibilityState === "visible") {
          startLoop();
        } else {
          stopLoop();
        }
      };
      const observer = new IntersectionObserver(entries => {
        inView = entries[0]?.isIntersecting ?? false;
        syncLoop();
      }, {
        threshold: 0
      });
      const section = sectionRef.current;
      if (section) {
        observer.observe(section);
      }
      document.addEventListener("visibilitychange", syncLoop);
      return () => {
        stopLoop();
        observer.disconnect();
        document.removeEventListener("visibilitychange", syncLoop);
      };
    };
    t3 = [isMobile, switchTab];
    $[4] = isMobile;
    $[5] = switchTab;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[8] !== isMobile || $[9] !== transitionState) {
    t4 = () => {
      if (isMobile) {
        setTransitionState(null);
        setTransitionProgress(1);
        if (transitionFrameRef.current !== null) {
          window.cancelAnimationFrame(transitionFrameRef.current);
          transitionFrameRef.current = null;
        }
        return;
      }
      if (!transitionState) {
        setTransitionProgress(1);
        return;
      }
      const start_0 = transitionStartRef.current ?? performance.now();
      transitionStartRef.current = start_0;
      const tick_0 = now_0 => {
        const currentProgress = clamp((now_0 - start_0) / TAB_TRANSITION_MS, 0, 1);
        setTransitionProgress(currentProgress);
        if (currentProgress >= 1) {
          setTransitionState(null);
          transitionFrameRef.current = null;
          return;
        }
        transitionFrameRef.current = window.requestAnimationFrame(tick_0);
      };
      transitionFrameRef.current = window.requestAnimationFrame(tick_0);
      return () => {
        if (transitionFrameRef.current !== null) {
          window.cancelAnimationFrame(transitionFrameRef.current);
          transitionFrameRef.current = null;
        }
      };
    };
    t5 = [isMobile, transitionState];
    $[8] = isMobile;
    $[9] = transitionState;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className={styles.heading}>VAETKI Commerce</p>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("featureDetail.heading");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <RevealOnScroll delayMs={60} y={18} className={styles.headerBlock}><div className={styles.headerInner}>{t6}<p className={styles.headingSecondary}>{t7}</p></div></RevealOnScroll>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== activeIndex || $[18] !== switchTab || $[19] !== t) {
    t9 = FEATURE_TABS.map((tab, index) => <TabButton key={tab.key} label={t(`featureDetail.tabs.${tab.key}.label`)} active={index === activeIndex} fillRef={progressFillRef} onClick={() => switchTab(index)} />);
    $[17] = activeIndex;
    $[18] = switchTab;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <div className={`${styles.tabsScroller} ${styles.detailScroll}`}><div className={styles.tabsRow}>{t9}</div></div>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className={styles.tabsDivider} />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10) {
    t12 = <RevealOnScroll delayMs={170} y={18} className={styles.tabsBlock}><div className={styles.tabsInner}>{t10}{t11}</div></RevealOnScroll>;
    $[24] = t10;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== activeTab || $[27] !== transitionProgress || $[28] !== transitionState) {
    t13 = <div className={styles.contentColumn}>{transitionState ? <><div className={styles.contentLayer} style={{
          opacity: 1 - transitionProgress
        }}><FeatureTabContent tab={FEATURE_TABS[transitionState.from]} /></div><div className={styles.contentLayer} style={{
          opacity: transitionProgress
        }}><FeatureTabContent tab={FEATURE_TABS[transitionState.to]} /></div></> : <FeatureTabContent tab={activeTab} />}</div>;
    $[26] = activeTab;
    $[27] = transitionProgress;
    $[28] = transitionState;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = featureVideoSrcs[activeIndex] ?? featureVideoSrcs[0];
  let t15;
  if ($[30] !== t14) {
    t15 = <div className={styles.mediaColumn}><ShowcaseHero src={t14} /></div>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t15) {
    t16 = <div className={styles.mainBlock}><div className={styles.mainGrid}>{t13}{t15}</div></div>;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t12 || $[36] !== t16 || $[37] !== t8) {
    t17 = <section ref={sectionRef} id="feature-detail-tabs" data-section="feature-detail-tabs" className={styles.section}><div className={styles.container}>{t8}{t12}{t16}</div></section>;
    $[35] = t12;
    $[36] = t16;
    $[37] = t8;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
};